import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
const INSIDE_PAGES = [{
  url: 'https://cdn.magicpatterns.com/uploads/gjSU1eKjiretFrvibwyG83/capture_260119_194525.png',
  alt: 'Página sobre recetas saludables'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/3aE6oDLXp4S5UV7WLgr42Q/capture_260119_194509.png',
  alt: 'Página sobre estrategias laborales'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/mVVgcpFKkrkAD1orBsZ6cq/capture_260119_194457.png',
  alt: 'Página sobre sostener hábitos'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/vYH5qQ4xEsw9zpRbsPi9AY/capture_260119_194358.png',
  alt: 'Página sobre ideales femeninos'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/ffUvZXvdyBwLnvbnoEuhct/capture_260119_194335.png',
  alt: 'Página sobre culpa y mandatos'
}];
export function InsidePagesSection() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };
  const closeLightbox = () => {
    setSelectedImage(null);
  };
  const nextImage = () => {
    setSelectedImage(prev => prev !== null ? (prev + 1) % INSIDE_PAGES.length : 0);
  };
  const prevImage = () => {
    setSelectedImage(prev => prev !== null ? (prev - 1 + INSIDE_PAGES.length) % INSIDE_PAGES.length : 0);
  };
  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % INSIDE_PAGES.length);
  };
  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + INSIDE_PAGES.length) % INSIDE_PAGES.length);
  };
  return <section className="py-12 px-4 bg-gradient-to-b from-white to-blue-50/20" data-id="element-22">
      <div className="max-w-4xl mx-auto" data-id="element-23">
        {/* Header */}
        <div className="text-center mb-10" data-id="element-24">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-700 mb-3" data-id="element-25">
            Un vistazo al interior del libro
          </h2>
          <p className="text-base text-gray-500 max-w-xl mx-auto leading-relaxed" data-id="element-26">
            Lecturas cortas, ejercicios simples y espacios para respirar.
          </p>
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative" data-id="element-27">
          <div className="relative overflow-hidden rounded-2xl shadow-lg" data-id="element-28">
            <motion.img key={currentIndex} initial={{
            opacity: 0,
            x: 100
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -100
          }} transition={{
            duration: 0.3
          }} src={INSIDE_PAGES[currentIndex].url} alt={INSIDE_PAGES[currentIndex].alt} className="w-full h-auto cursor-pointer" onClick={() => openLightbox(currentIndex)} data-id="element-29" />
          </div>

          {/* Carousel Controls */}
          <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-colors" aria-label="Anterior" data-id="element-30">
            <ChevronLeft className="w-5 h-5 text-gray-700" data-id="element-31" />
          </button>
          <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-colors" aria-label="Siguiente" data-id="element-32">
            <ChevronRight className="w-5 h-5 text-gray-700" data-id="element-33" />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-4" data-id="element-34">
            {INSIDE_PAGES.map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} className={`w-2 h-2 rounded-full transition-all ${index === currentIndex ? 'bg-teal-500 w-6' : 'bg-gray-300'}`} aria-label={`Ir a página ${index + 1}`} data-id="element-35" />)}
          </div>
        </div>

        {/* Desktop Grid - Optimized for compact 3-column layout */}
        <div className="hidden md:grid grid-cols-3 gap-4 max-w-3xl mx-auto" data-id="element-36">
          {INSIDE_PAGES.map((page, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="group relative cursor-pointer" onClick={() => openLightbox(index)} data-id="element-37">
              <div className="relative overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 bg-white p-1.5" data-id="element-38">
                <img src={page.url} alt={page.alt} className="w-full h-auto rounded-md transform group-hover:scale-105 transition-transform duration-300" data-id="element-39" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center" data-id="element-40">
                  <span className="text-white font-semibold bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs" data-id="element-41">
                    Ver en grande
                  </span>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Lightbox */}
        <AnimatePresence data-id="element-42">
          {selectedImage !== null && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeLightbox} data-id="element-43">
              <button onClick={closeLightbox} className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-2 rounded-full transition-colors" aria-label="Cerrar" data-id="element-44">
                <X className="w-6 h-6 text-white" data-id="element-45" />
              </button>

              <button onClick={e => {
            e.stopPropagation();
            prevImage();
          }} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 rounded-full transition-colors" aria-label="Anterior" data-id="element-46">
                <ChevronLeft className="w-6 h-6 text-white" data-id="element-47" />
              </button>

              <motion.img key={selectedImage} initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} exit={{
            scale: 0.9,
            opacity: 0
          }} src={INSIDE_PAGES[selectedImage].url} alt={INSIDE_PAGES[selectedImage].alt} className="max-w-full max-h-[90vh] rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-48" />

              <button onClick={e => {
            e.stopPropagation();
            nextImage();
          }} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 rounded-full transition-colors" aria-label="Siguiente" data-id="element-49">
                <ChevronRight className="w-6 h-6 text-white" data-id="element-50" />
              </button>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full" data-id="element-51">
                {selectedImage + 1} / {INSIDE_PAGES.length}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}